import { Box, Button, CircularProgress, InputAdornment, OutlinedInput, TextField, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  sxButtons,
  sxError,
  sxLabel,
  sxStyleBtn,
  sxTextField,
  sxTitle,
  sxValidationMessage,
} from "./sxProps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import DefaultButton from "../../../../components/Accessibility/DefaultButton";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import { atualizarEnderPostal, atualizarEnderPostalPagador, getLogradrouroPorCep } from "../../../../services/BeneficiarioService";
import LoadingApp from "../../../../components/LoadingApp";
import { ModalDefault } from "../../../../components/ModalDefault";
import { cepMask } from "../../../../utils/CepMask";
import { EnderPostal } from "../../../../interfaces/entities/MeuCadastro/EnderPostal";
import { UpdateEnderRequest } from "../../../../interfaces/entities/MeuCadastro/UpdateEnderRequest";
import ModalFeedBack from "../../../../components/ModalFeedBack";
import { ChangeAdressDataValidation } from "../../../../interfaces/validation/MeuCadastro/ChangeAdressDataValidation";
import { sxInputCep, sxInputEmail, sxInputPhone, sxLabelDisabled } from "../sxProps";

interface ModalEditAdressProps {
  open: boolean;
  onClose: () => void;
  openModalSuccessNoButton: () => void;
  title: string
  enderPostal?: EnderPostal[];
  tipoEndereco?: 'RESIDENCIAL' | 'COBRANCA';
}

export const ModalEditAdress: React.FC<ModalEditAdressProps> = ({
  open,
  onClose,
  enderPostal,
  title,
  tipoEndereco,
  openModalSuccessNoButton
}) => {
  const mobileScreen = useMediaQuery("(max-width: 768px)");
  const {
    contrastBorderAccessibility,
    textStyleAccessibility,
  } = useAccessibility();
  const [loading, setLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [loadingCep, setLoadingCep] = useState<boolean>(false);


  const endereco = enderPostal?.find((endereco) => {
    return endereco.tipo === tipoEndereco;
  });

  const formik = useFormik({
    initialValues: {
      cep: endereco?.cep ? cepMask(endereco.cep) : "",
      logradouro: endereco?.logradouro || "",
      numero: endereco?.numero || "",
      complemento: endereco?.complemento || "",
      bairro: endereco?.bairro || "",
      cidade: endereco?.cidade || "",
      estado: endereco?.estado || "",
      dtInicValidade: endereco?.dtInicValidade,
    },
    validationSchema: ChangeAdressDataValidation,
    onSubmit: (values) => {
      handleUpdateEnderPostal(values);
    },
    validateOnChange: true,
  });

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const handleUpdateEnderPostal = async (endereco: UpdateEnderRequest) => {
    setLoading(true);
    let response;
    if (tipoEndereco === "COBRANCA") {
      response = await atualizarEnderPostalPagador(endereco);
    } else {
      response = await atualizarEnderPostal(endereco);
    }
    if (response?.status === 200) {
      openModalSuccessNoButton();
      onClose();
    } else {
      const errorMessage = response as any;
      const mensagem = errorMessage?.response?.data?.mensagem;
      openModalError(
        "Erro",
        mensagem
          ? mensagem
          : "Ocorreu um erro ao salvar os dados, tente novamente em instantes."
      );
    }
    setLoading(false);
  };

  const findAdressByCep = async (cep: string) => {
    setLoadingCep(true);
    const response = await getLogradrouroPorCep(cep.replace("-", ""));
    if (response?.status === 200) {
      if (!!response.data.length) {
        const enderecoCep = response.data;
        updateFormikValues(enderecoCep.at(0)!);
      } else {
        const invalidCepMessage = "CEP inválido";
        formik.setFieldError("cep", invalidCepMessage);
      }
    } else {
      openModalError(
        "Erro",
        "Ocorreu um erro ao procurar pelo CEP, tente novamente em instantes."
      );
    }
    setLoadingCep(false);
  };

  const updateFormikValues = (endereco: EnderPostal) => {
    formik.setFieldValue("logradouro", endereco?.logradouro);
    formik.setFieldValue("bairro", endereco?.bairro);
    formik.setFieldValue("cidade", endereco?.cidade);
    formik.setFieldValue("estado", endereco?.estado);
    formik.setFieldValue("numero", "");
    formik.setFieldValue("complemento", "");
  };

  useEffect(() => {
    if (endereco) {
      const values = {
        cep: endereco?.cep ? cepMask(endereco.cep) : "",
        logradouro: endereco?.logradouro || "",
        numero: endereco?.numero || "",
        complemento: endereco?.complemento || "",
        bairro: endereco?.bairro || "",
        cidade: endereco?.cidade || "",
        estado: endereco?.estado || "",
        dtInicValidade: endereco?.dtInicValidade,
      }
      formik.setValues(values);
    }
  }, [endereco]);


  return (
    <ModalDefault
      open={open}
      title={title}
      sxTitle={sxTitle}
      onClose={onClose}
      maxWidth={578}
    >
      <Box
        style={{
          ...textStyleAccessibility,
          ...contrastBorderAccessibility,
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="personal-data-confirmation-container" style={{ marginBottom: "30px" }}>
            <div className="flex-col">
              <Typography
                component="label"
                htmlFor="cep"
                style={textStyleAccessibility}
                sx={sxLabel}
              >
                CEP
              </Typography>
              <Box display={"flex"} flexDirection={"row"}>
                <OutlinedInput
                  id="cep"
                  style={textStyleAccessibility}
                  fullWidth={mobileScreen}
                  type={"text"}
                  inputMode="numeric"
                  disabled={loadingCep}
                  value={formik.values.cep}
                  onChange={(e) => {
                    const cepValue = e.target.value;
                    formik.setFieldValue("cep", cepMask(cepValue));
                    if (cepValue.replace("-", "").length === 8) {
                      findAdressByCep(cepValue);
                    }
                  }}
                  name="cep"
                  error={!!formik.errors.cep}
                  maxRows={1}
                  inputProps={{ 
                    maxLength: 9,
                    'aria-describedby': formik.errors.cep ? 'cep-error' : undefined,
                  }}
                  sx={sxInputCep}
                  endAdornment={
                    loadingCep ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} sx={{ zIndex: "1" }} />
                      </InputAdornment>
                    ) : null
                  }
                />
              </Box>
              {formik.errors.cep && (
                <div
                  id="cep-error"
                  className="container-validation-message" 
                  aria-live="assertive"
                >
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size="sm"
                    color="#D84141"
                  />
                  <Typography sx={sxError}>
                    {formik.errors.cep}
                  </Typography>
                </div>
              )}
            </div>
            <div>
              <Typography style={textStyleAccessibility} sx={sxLabelDisabled}>
                Rua
              </Typography>
              <OutlinedInput
                style={textStyleAccessibility}
                fullWidth={mobileScreen}
                type={"text"}
                tabIndex={0}
                disabled
                value={formik.values.logradouro}
                onChange={(e) => {
                  formik.setFieldValue("logradouro", e.target.value);
                }}
                name="logradouro"
                error={!!formik.errors.logradouro}
                maxRows={1}
                sx={sxInputEmail}
              />
            </div>
            <div className="cel-tel-input">
              <div className="flex-col">
                <Typography
                  component="label"
                  htmlFor="numero"
                  style={textStyleAccessibility}
                  sx={sxLabel}
                >
                  Número
                </Typography>
                <OutlinedInput
                  id="numero"
                  style={textStyleAccessibility}
                  fullWidth={mobileScreen}
                  type={"number"}
                  value={formik.values.numero}
                  onChange={(e) => {
                    formik.setFieldValue("numero", e.target.value.toUpperCase());
                  }}
                  name="numero"
                  error={!!formik.errors.numero}
                  inputProps={{ 
                    maxLength: 6,
                    'aria-describedby': formik.errors.numero ? 'num-error' : undefined,
                  }}
                  maxRows={1}
                  sx={sxInputPhone}
                />
                {formik.errors.numero && (
                  <div
                    id="num-error"
                    className="container-validation-message" 
                    aria-live="assertive"
                  >
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography sx={sxError}>
                      {formik.errors.numero}
                    </Typography>
                  </div>
                )}
              </div>
              <div className="flex-col">
                <Typography
                  component="label"
                  htmlFor="complemento"
                  style={textStyleAccessibility}
                  sx={sxLabel}
                >
                  Complemento
                </Typography>
                <OutlinedInput
                  id="complemento"
                  style={textStyleAccessibility}
                  fullWidth={mobileScreen}
                  type={"text"}
                  value={formik.values.complemento}
                  onChange={(e) => {
                    formik.setFieldValue("complemento", e.target.value);
                  }}
                  name="complemento"
                  error={!!formik.errors.complemento}
                  maxRows={1}
                  inputProps={{ maxLength: 15 }}
                  sx={sxInputPhone}
                />
                {formik.errors.complemento && (
                  <div className="container-validation-message">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography tabIndex={0} sx={sxError}>
                      {formik.errors.complemento}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Typography style={textStyleAccessibility} sx={sxLabelDisabled}>
                Bairro
              </Typography>
              <OutlinedInput
                style={textStyleAccessibility}
                fullWidth={mobileScreen}
                type={"text"}
                disabled
                tabIndex={0}
                value={formik.values.bairro}
                onChange={(e) => {
                  formik.setFieldValue("bairro", e.target.value);
                }}
                name="bairro"
                error={!!formik.errors.bairro}
                maxRows={1}
                sx={sxInputEmail}
              />
              {formik.errors.bairro && (
                <div className="container-validation-message">
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size="sm"
                    color="#D84141"
                  />
                  <Typography tabIndex={0} sx={sxError}>
                    {formik.errors.bairro}
                  </Typography>
                </div>
              )}
            </div>
            <div className="cel-tel-input">
              <div>
                <Typography style={textStyleAccessibility} sx={sxLabelDisabled}>
                  Cidade
                </Typography>
                <OutlinedInput
                  style={textStyleAccessibility}
                  fullWidth={mobileScreen}
                  type={"text"}
                  disabled
                  tabIndex={0}
                  value={formik.values.cidade}
                  onChange={(e) => {
                    formik.setFieldValue("cidade", e.target.value);
                  }}
                  name="cidade"
                  error={!!formik.errors.cidade}
                  maxRows={1}
                  sx={sxInputPhone}
                />
                {formik.errors.cidade && (
                  <div className="container-validation-message">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography tabIndex={0} sx={sxError}>
                      {formik.errors.cidade}
                    </Typography>
                  </div>
                )}
              </div>
              <div>
                <Typography style={textStyleAccessibility} sx={sxLabelDisabled}>
                  Estado
                </Typography>
                <OutlinedInput
                  style={textStyleAccessibility}
                  fullWidth={mobileScreen}
                  type={"text"}
                  disabled
                  tabIndex={0}
                  value={formik.values.estado}
                  onChange={(e) => {
                    formik.setFieldValue("estado", e.target.value);
                  }}
                  name="estado"
                  error={!!formik.errors.estado}
                  maxRows={1}
                  inputProps={{ maxLength: 2 }}
                  sx={sxInputPhone}
                />
                {formik.errors.estado && (
                  <div className="container-validation-message">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography tabIndex={0} sx={sxError}>
                      {formik.errors.estado}
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Box sx={sxButtons}>
            <Button
              variant="outlined"
              sx={sxStyleBtn}
              onClick={() => {
                formik.resetForm();
                onClose();
              }}
            >
              <Typography style={{ color: '#00995D' }}>Cancelar</Typography>
            </Button>
            <DefaultButton
              text="Salvar"
              type="submit"
              onClick={formik.submitForm}
            />
          </Box>
        </form>
        <LoadingApp showLoading={loading} />
        <ModalFeedBack
          showModal={!!modalFeedback}
          icon={modalFeedback?.icon as 'success' | 'error'}
          textButton={modalFeedback?.textButton as string}
          title={modalFeedback?.title as string}
          subTitle={modalFeedback?.subTitle as string}
          showButtonModal={modalFeedback?.showButtonModal}
          handleShowModal={modalFeedback?.closeAction}
          handleButtonModal={modalFeedback?.buttonAction}
        />
      </Box>
    </ModalDefault>
  );
};

export default ModalEditAdress;
